import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { agencyInfo } from '../../../mocks/dashboard';
import { bookingsData } from '../../../mocks/bookings';

interface LineItem {
  id: string;
  description: string;
  details: string;
  qty: number;
  unitPrice: number;
}

export default function NewInvoicePage() {
  const navigate = useNavigate();
  const brand = agencyInfo.brandColor;
  const [step, setStep] = useState(1);
  const [success, setSuccess] = useState(false);
  const [newInvNumber] = useState(`INV-2026-${1085 + Math.floor(Math.random() * 100)}`);

  // Step 1: Customer
  const [linkBooking, setLinkBooking] = useState(true);
  const [selectedBookingId, setSelectedBookingId] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerAddress, setCustomerAddress] = useState('');

  // Step 2: Dates & notes
  const [issuedDate, setIssuedDate] = useState(new Date().toISOString().split('T')[0]);
  const [dueDate, setDueDate] = useState('');
  const [notes, setNotes] = useState('');
  const [currency] = useState(agencyInfo.currency);

  // Step 3: Line Items
  const [lineItems, setLineItems] = useState<LineItem[]>([
    { id: 'li1', description: '', details: '', qty: 1, unitPrice: 0 },
  ]);
  const [discountPct, setDiscountPct] = useState(0);
  const [taxPct, setTaxPct] = useState(5);

  const selectedBooking = bookingsData.find((b) => b.id === selectedBookingId);

  const addLineItem = () => {
    setLineItems((prev) => [...prev, { id: `li${Date.now()}`, description: '', details: '', qty: 1, unitPrice: 0 }]);
  };

  const removeLineItem = (id: string) => {
    setLineItems((prev) => prev.filter((li) => li.id !== id));
  };

  const updateLineItem = (id: string, field: keyof LineItem, value: string | number) => {
    setLineItems((prev) => prev.map((li) => li.id === id ? { ...li, [field]: value } : li));
  };

  const subtotal = lineItems.reduce((s, li) => s + li.qty * li.unitPrice, 0);
  const discountAmt = (subtotal * discountPct) / 100;
  const taxAmt = ((subtotal - discountAmt) * taxPct) / 100;
  const total = subtotal - discountAmt + taxAmt;

  const fmtAmount = (n: number) =>
    `${currency} ${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const handleBookingSelect = (id: string) => {
    setSelectedBookingId(id);
    const b = bookingsData.find((bk) => bk.id === id);
    if (b) {
      setCustomerName(b.customer);
      setCustomerEmail(b.customerEmail);
      setCustomerPhone(b.customerPhone);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSuccess(true);
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50/50 flex items-center justify-center p-8">
        <div className="bg-white rounded-2xl border border-gray-100 p-12 max-w-md w-full text-center">
          <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: `${brand}15` }}>
            <i className="ri-checkbox-circle-line text-3xl" style={{ color: brand }} />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Invoice Created!</h2>
          <p className="text-gray-500 text-sm mb-2">Invoice <span className="font-semibold" style={{ color: brand }}>{newInvNumber}</span> has been created.</p>
          <p className="text-gray-400 text-xs mb-6">Total: {fmtAmount(total)}</p>
          <div className="flex gap-3">
            <button
              onClick={() => navigate('/invoices')}
              className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 cursor-pointer"
            >
              View All Invoices
            </button>
            <button
              onClick={() => { setSuccess(false); setStep(1); setLineItems([{ id: 'li1', description: '', details: '', qty: 1, unitPrice: 0 }]); }}
              className="flex-1 py-2.5 rounded-xl text-white text-sm font-semibold cursor-pointer"
              style={{ backgroundColor: brand }}
            >
              Create Another
            </button>
          </div>
        </div>
      </div>
    );
  }

  const steps = [
    { n: 1, label: 'Customer' },
    { n: 2, label: 'Details' },
    { n: 3, label: 'Line Items' },
    { n: 4, label: 'Review' },
  ];

  return (
    <div className="min-h-screen bg-gray-50/50 p-8">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <button
          onClick={() => navigate('/invoices')}
          className="w-9 h-9 flex items-center justify-center rounded-xl border border-gray-200 bg-white hover:bg-gray-50 text-gray-500 cursor-pointer"
        >
          <i className="ri-arrow-left-line" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">New Invoice</h1>
          <p className="text-sm text-gray-400">{newInvNumber}</p>
        </div>
      </div>

      {/* Step Indicator */}
      <div className="flex items-center gap-0 mb-8 max-w-xl">
        {steps.map((s, idx) => (
          <div key={s.n} className="flex items-center flex-1">
            <div className="flex flex-col items-center">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-all ${
                  step > s.n ? 'text-white' : step === s.n ? 'text-white' : 'bg-gray-100 text-gray-400'
                }`}
                style={step >= s.n ? { backgroundColor: brand } : {}}
              >
                {step > s.n ? <i className="ri-check-line text-sm" /> : s.n}
              </div>
              <span className={`text-xs mt-1 font-medium whitespace-nowrap ${step === s.n ? 'text-gray-700' : 'text-gray-400'}`}>{s.label}</span>
            </div>
            {idx < steps.length - 1 && (
              <div className={`flex-1 h-0.5 mb-4 mx-2 ${step > s.n ? '' : 'bg-gray-100'}`}
                style={step > s.n ? { backgroundColor: brand } : {}} />
            )}
          </div>
        ))}
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-3 gap-6">
          {/* Main Form */}
          <div className="col-span-2 bg-white rounded-2xl border border-gray-100 p-6">

            {step === 1 && (
              <div className="space-y-5">
                <h2 className="font-bold text-gray-900">Customer Information</h2>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setLinkBooking(true)}
                    className={`flex-1 py-2.5 rounded-xl border text-sm font-medium cursor-pointer ${linkBooking ? 'border-transparent text-white' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                    style={linkBooking ? { backgroundColor: brand } : {}}
                  >
                    Link to Booking
                  </button>
                  <button
                    type="button"
                    onClick={() => setLinkBooking(false)}
                    className={`flex-1 py-2.5 rounded-xl border text-sm font-medium cursor-pointer ${!linkBooking ? 'border-transparent text-white' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                    style={!linkBooking ? { backgroundColor: brand } : {}}
                  >
                    Manual Entry
                  </button>
                </div>

                {linkBooking && (
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1.5">Select Booking</label>
                    <select
                      value={selectedBookingId}
                      onChange={(e) => handleBookingSelect(e.target.value)}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none cursor-pointer"
                    >
                      <option value="">— Select a booking —</option>
                      {bookingsData.map((b) => (
                        <option key={b.id} value={b.id}>{b.id} — {b.customer} ({b.destination})</option>
                      ))}
                    </select>
                    {selectedBooking && (
                      <div className="mt-3 bg-gray-50 rounded-xl p-3 text-xs text-gray-600 space-y-1">
                        <p><span className="font-medium">Service:</span> {selectedBooking.service}</p>
                        <p><span className="font-medium">Dates:</span> {selectedBooking.travelDate} → {selectedBooking.returnDate}</p>
                        <p><span className="font-medium">Amount:</span> {agencyInfo.currency} {selectedBooking.totalAmount.toLocaleString()}</p>
                      </div>
                    )}
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="block text-xs font-medium text-gray-600 mb-1.5">Customer Name</label>
                    <input type="text" required value={customerName} onChange={(e) => setCustomerName(e.target.value)}
                      placeholder="Full name" className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1.5">Email</label>
                    <input type="email" value={customerEmail} onChange={(e) => setCustomerEmail(e.target.value)}
                      placeholder="email@example.com" className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1.5">Phone</label>
                    <input type="text" value={customerPhone} onChange={(e) => setCustomerPhone(e.target.value)}
                      placeholder="+971 50 000 0000" className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none" />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs font-medium text-gray-600 mb-1.5">Address</label>
                    <input type="text" value={customerAddress} onChange={(e) => setCustomerAddress(e.target.value)}
                      placeholder="Full billing address" className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none" />
                  </div>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-5">
                <h2 className="font-bold text-gray-900">Invoice Details</h2>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1.5">Issued Date</label>
                    <input type="date" value={issuedDate} onChange={(e) => setIssuedDate(e.target.value)}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none cursor-pointer" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1.5">Due Date</label>
                    <input type="date" required value={dueDate} onChange={(e) => setDueDate(e.target.value)}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none cursor-pointer" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">Notes (optional)</label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={4}
                    maxLength={500}
                    placeholder="Payment terms, special instructions, or personal message..."
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none resize-none"
                  />
                  <p className="text-xs text-gray-400 text-right mt-1">{notes.length}/500</p>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-5">
                <div className="flex items-center justify-between">
                  <h2 className="font-bold text-gray-900">Line Items</h2>
                  <button type="button" onClick={addLineItem}
                    className="flex items-center gap-1.5 text-sm font-medium cursor-pointer" style={{ color: brand }}>
                    <i className="ri-add-line" />
                    Add Item
                  </button>
                </div>
                <div className="space-y-3">
                  {lineItems.map((item, idx) => (
                    <div key={item.id} className="bg-gray-50 rounded-xl p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-gray-400">Item {idx + 1}</span>
                        {lineItems.length > 1 && (
                          <button type="button" onClick={() => removeLineItem(item.id)}
                            className="w-6 h-6 flex items-center justify-center rounded-lg hover:bg-rose-100 text-gray-400 hover:text-rose-500 cursor-pointer">
                            <i className="ri-close-line text-xs" />
                          </button>
                        )}
                      </div>
                      <input type="text" required value={item.description}
                        onChange={(e) => updateLineItem(item.id, 'description', e.target.value)}
                        placeholder="Service description" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none bg-white" />
                      <input type="text" value={item.details}
                        onChange={(e) => updateLineItem(item.id, 'details', e.target.value)}
                        placeholder="Additional details (optional)" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none bg-white" />
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <label className="block text-xs text-gray-400 mb-1">Qty</label>
                          <input type="number" min="1" value={item.qty}
                            onChange={(e) => updateLineItem(item.id, 'qty', Number(e.target.value))}
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none bg-white" />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-400 mb-1">Unit Price ({currency})</label>
                          <input type="number" min="0" step="0.01" value={item.unitPrice}
                            onChange={(e) => updateLineItem(item.id, 'unitPrice', Number(e.target.value))}
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none bg-white" />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-400 mb-1">Total</label>
                          <div className="px-3 py-2 bg-white border border-gray-100 rounded-lg text-sm font-semibold text-gray-700">
                            {fmtAmount(item.qty * item.unitPrice)}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="border-t border-gray-100 pt-4 space-y-3">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1.5">Discount (%)</label>
                      <input type="number" min="0" max="100" value={discountPct}
                        onChange={(e) => setDiscountPct(Number(e.target.value))}
                        className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1.5">VAT (%)</label>
                      <input type="number" min="0" max="100" value={taxPct}
                        onChange={(e) => setTaxPct(Number(e.target.value))}
                        className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none" />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {step === 4 && (
              <div className="space-y-6">
                <h2 className="font-bold text-gray-900">Review Invoice</h2>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="space-y-2">
                    <p className="text-xs text-gray-400 uppercase font-medium tracking-wider">Customer</p>
                    <p className="font-semibold text-gray-800">{customerName}</p>
                    <p className="text-gray-500">{customerEmail}</p>
                    <p className="text-gray-500">{customerPhone}</p>
                    {customerAddress && <p className="text-gray-500 text-xs">{customerAddress}</p>}
                  </div>
                  <div className="space-y-2 text-right">
                    <p className="text-xs text-gray-400 uppercase font-medium tracking-wider">Invoice Info</p>
                    <p className="font-semibold" style={{ color: brand }}>{newInvNumber}</p>
                    <p className="text-gray-500">Issued: {new Date(issuedDate).toLocaleDateString('en-GB')}</p>
                    {dueDate && <p className="text-gray-500">Due: {new Date(dueDate).toLocaleDateString('en-GB')}</p>}
                    {selectedBookingId && <p className="text-gray-500">Ref: {selectedBookingId}</p>}
                  </div>
                </div>
                <div className="border border-gray-100 rounded-xl overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 text-xs text-gray-400">
                        <th className="text-left px-4 py-3">Description</th>
                        <th className="text-center px-3 py-3">Qty</th>
                        <th className="text-right px-3 py-3">Price</th>
                        <th className="text-right px-4 py-3">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {lineItems.map((item) => (
                        <tr key={item.id}>
                          <td className="px-4 py-3">
                            <p className="font-medium text-gray-800">{item.description || '—'}</p>
                            {item.details && <p className="text-xs text-gray-400">{item.details}</p>}
                          </td>
                          <td className="px-3 py-3 text-center text-gray-600">{item.qty}</td>
                          <td className="px-3 py-3 text-right text-gray-600">{fmtAmount(item.unitPrice)}</td>
                          <td className="px-4 py-3 text-right font-semibold text-gray-800">{fmtAmount(item.qty * item.unitPrice)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="border-t border-gray-100 px-4 py-3 bg-gray-50 space-y-1.5 text-sm">
                    <div className="flex justify-between text-gray-500">
                      <span>Subtotal</span><span>{fmtAmount(subtotal)}</span>
                    </div>
                    {discountAmt > 0 && (
                      <div className="flex justify-between text-emerald-600">
                        <span>Discount ({discountPct}%)</span><span>- {fmtAmount(discountAmt)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-gray-500">
                      <span>VAT ({taxPct}%)</span><span>{fmtAmount(taxAmt)}</span>
                    </div>
                    <div className="flex justify-between font-bold text-base text-gray-900 pt-2 border-t border-gray-200">
                      <span>Total</span>
                      <span style={{ color: brand }}>{fmtAmount(total)}</span>
                    </div>
                  </div>
                </div>
                {notes && (
                  <div className="bg-amber-50 rounded-xl p-4 text-xs text-amber-700">
                    <p className="font-medium mb-1">Notes</p>
                    <p>{notes}</p>
                  </div>
                )}
              </div>
            )}

            {/* Navigation */}
            <div className="flex justify-between mt-8 pt-6 border-t border-gray-100">
              {step > 1 ? (
                <button type="button" onClick={() => setStep((s) => s - 1)}
                  className="px-5 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 cursor-pointer">
                  Previous
                </button>
              ) : (
                <button type="button" onClick={() => navigate('/invoices')}
                  className="px-5 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 cursor-pointer">
                  Cancel
                </button>
              )}
              {step < 4 ? (
                <button type="button" onClick={() => setStep((s) => s + 1)}
                  className="px-6 py-2.5 rounded-xl text-white text-sm font-semibold cursor-pointer"
                  style={{ backgroundColor: brand }}>
                  Continue
                </button>
              ) : (
                <button type="submit"
                  className="px-6 py-2.5 rounded-xl text-white text-sm font-semibold cursor-pointer"
                  style={{ backgroundColor: brand }}>
                  Create Invoice
                </button>
              )}
            </div>
          </div>

          {/* Summary Panel */}
          <div className="bg-white rounded-2xl border border-gray-100 p-5 h-fit">
            <p className="text-xs text-gray-400 uppercase font-medium tracking-wider mb-4">Invoice Summary</p>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between text-gray-600">
                <span>Invoice #</span>
                <span className="font-medium" style={{ color: brand }}>{newInvNumber}</span>
              </div>
              <div className="flex justify-between text-gray-600">
                <span>Customer</span>
                <span className="font-medium text-gray-800 truncate max-w-28">{customerName || '—'}</span>
              </div>
              <div className="flex justify-between text-gray-600">
                <span>Items</span>
                <span className="font-medium text-gray-800">{lineItems.length}</span>
              </div>
              <div className="flex justify-between text-gray-600">
                <span>Subtotal</span>
                <span className="font-medium text-gray-800">{fmtAmount(subtotal)}</span>
              </div>
              {discountAmt > 0 && (
                <div className="flex justify-between text-emerald-600">
                  <span>Discount</span>
                  <span>- {fmtAmount(discountAmt)}</span>
                </div>
              )}
              <div className="flex justify-between text-gray-600">
                <span>VAT ({taxPct}%)</span>
                <span className="font-medium text-gray-800">{fmtAmount(taxAmt)}</span>
              </div>
              <div className="pt-2 border-t border-gray-100 flex justify-between font-bold text-base text-gray-900">
                <span>Total</span>
                <span style={{ color: brand }}>{fmtAmount(total)}</span>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
